import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Typography,
} from "@mui/material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import BudgetServiceItem from "../ServiceItem";
import {
  AttachedFiles,
  BudgetItemTypeEnum,
  CategoryTotals,
  CalcOption,
  IPaymentSolicitationByType,
  MainAppliedTaxes,
} from "../../../../types/budget/budget";
import { EventScopeEnum } from "../../../../types/project/enums";
import TransferServiceSupplierModal from "../TransferServiceSupplierModal";
import { TransferTarget } from "../TransferCategorySupplierModal";
import { useTransferTargets } from "../hooks/useTransferTargets";
import {
  parseSupplierId,
  resolveNewSupplierId,
} from "../../../../utils/budget-supplier-id";
import { ServiceOptions } from "../../../../utils/serviceTypesFromBudget";
import { projectTransferredServiceValue } from "../../../../utils/budget-service-transfer-projection";
import { getPhaseValuesFromService } from "../../../../utils/budget-service-writes";
import {
  dispatchAddService,
  isAddServicePointerGestureLocked,
  prepareAddServiceFromPointer,
} from "../../../../utils/add-service-gesture";
import {
  getBudgetServiceGroupKey,
  useBudgetActions,
  useBudgetMeta,
  useBudgetServicesState,
  useBudgetSuppliersState,
} from "../../../../contexts/BudgetContext/projectbudgets.context";
import { useLocation } from "react-router-dom";
import { BiChevronDown, BiReset } from "react-icons/bi";
import { useProject } from "../../../../contexts/project.context";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ServiceData,
  ServiceValues,
} from "../../../../types/budget/service-item";
import { toast } from "react-toastify";
import PaymentMethodInfo from "../PaymentMethodInfo";
import FileUpload from "../FileUpload";
import { createOptionsFromEnum } from "../../../../utils/formatters";
import { HiOutlinePlus } from "react-icons/hi";
import { MdDragIndicator } from "react-icons/md";
import ChangelingMonetaryInput from "../../../UI/ChangelingMonetaryInput";
import ExchangeFields from "../../../UI/ExchangeFields";
import { LabeledInput } from "../../../UI/LabeledInput";
import BaseMonetaryData from "../BaseMonetaryData";
import CoinData from "../../../../utils/Common-Currency.json";
import { getCurrencyDisplayLabel } from "../../../../utils/currencyDisplay";
import { sortCurrencyOptions } from "../../../../utils/currencySort";
import { TabActive } from "../../../../types/project.type";
import BaseValuesTotal from "../BaseValuesTotal";
import { BudgetService } from "../../../../services/budget.service";
import AppliedTaxes from "../AppliedTaxes";
import { getEventExchange } from "../../../../utils/exchangeManager";
import CategoryAccordionSummary from "./CategoryAccordionSummary";
import {
  CATEGORY_ROW_BACKGROUND,
  categoryAccordionSx,
  categoryAccordionTransitionProps,
} from "./categoryAccordionUi";
import { useCategoryTotals } from "./useCategoryTotals";

type Props = {
  type: keyof typeof BudgetItemTypeEnum;
  supplierId: string;
  supplierName: string;
  /**
   * Restringe a categoria aos serviços de UM agrupamento (visão "Por
   * Agrupamento"). Ausente ⇒ a categoria mostra todos os serviços do par
   * (fornecedor, categoria), que é o comportamento da visão "Por Fornecedor".
   */
  groupId?: string;
  /**
   * Totais do CABEÇALHO já recortados pelo agrupamento.
   *
   * A árvore de totais agrega por (fornecedor, categoria) e não conhece
   * agrupamento — com `groupId` a categoria mostra só parte dos serviços, e o
   * total da árvore seria o do par inteiro. Quem passa `groupId` passa isto
   * junto (ver `utils/budget-group-totals.ts`).
   */
  totalsOverride?: CategoryTotals;
  /** Linha secundária no cabeçalho: o fornecedor, na visão agrupada. */
  headerSubtitle?: React.ReactNode;
  /** Alça de arrasto no cabeçalho, antes do número. */
  headerLeadingSlot?: React.ReactNode;
  paymentInfo: IPaymentSolicitationByType;
  panel: string;
  baseValuePanel: string;
  expanded: boolean;
  baseValueExpanded: boolean;
  onRemoveItem: (type: keyof typeof BudgetItemTypeEnum) => void;
  onMoveBillingModel: (type: keyof typeof BudgetItemTypeEnum) => void;
  onTransferSupplier: (type: keyof typeof BudgetItemTypeEnum) => void;
  onChange: (
    panel: string
  ) => (_event: React.SyntheticEvent, isExpanded: boolean) => void;
  index: number;
  onChangeBaseValue: (
    panel: string
  ) => (_event: React.SyntheticEvent, isExpanded: boolean) => void;
};

const coinOptions = sortCurrencyOptions(
  CoinData.map((el) => ({
    value: el.code,
    label: getCurrencyDisplayLabel(el.name),
  }))
);

const SERVICE_LIST_VIRTUALIZATION_THRESHOLD = 60;
const SERVICE_ROW_ESTIMATED_HEIGHT = 96;
const SERVICE_LIST_MAX_HEIGHT = 720;
const SERVICE_LIST_OVERSCAN = 8;

// Sortable wrapper component for each service item.
// Re-rendered for every row whenever `BudgetItem`'s render runs, so we
// memoize it below: per-keystroke edits in service A no longer pull this
// row's `useSortable` hook for every other service B≠A. The comparator is
// shape-aware and bails out only when every prop is reference-equal.
type SortableServiceItemProps = {
  service: any;
  index: number;
  type: keyof typeof BudgetItemTypeEnum;
  baseValueExchange: any;
  descriptionFocus: any;
  setDescriptionFocus: any;
  serviceFocus: any;
  setServiceFocus: any;
  hoveredServiceIndex: number | null;
  setHoveredServiceIndex: (idx: number | null) => void;
  handleAddServiceAtPosition: (positionInFilteredList: number) => void;
  disabled: boolean;
  mainAppliedTaxes: any;
  exchangeCurrency: string;
  categoryExchange?: {
    date: string;
    currency: string;
    value: number;
  };
  onTransferService?: (service: ServiceData) => void;
};

const SortableServiceItemImpl = ({
  service,
  index,
  type,
  baseValueExchange,
  descriptionFocus,
  setDescriptionFocus,
  serviceFocus,
  setServiceFocus,
  hoveredServiceIndex,
  setHoveredServiceIndex,
  handleAddServiceAtPosition,
  disabled,
  mainAppliedTaxes,
  exchangeCurrency,
  categoryExchange,
  onTransferService,
}: SortableServiceItemProps) => {
  const sortableId = service._id ?? `service-${index}-${type}`;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onMouseEnter={() => {
        if (isAddServicePointerGestureLocked()) return;
        setHoveredServiceIndex(index);
      }}
      onMouseLeave={() => {
        if (isAddServicePointerGestureLocked()) return;
        setHoveredServiceIndex(null);
      }}
      sx={{ position: "relative" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          border: isDragging ? "2px dashed #1361A4" : "1px solid transparent",
          borderRadius: 1,
          padding: isDragging ? 1 : 0,
          backgroundColor: isDragging ? "#f0f7ff" : "transparent",
        }}
      >
        {!disabled && (
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: "grab",
              "&:active": { cursor: "grabbing" },
              display: "flex",
              alignItems: "center",
              padding: "8px",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
              },
            }}
          >
            <MdDragIndicator size={24} color="#666" />
          </Box>
        )}
        <Box flex={1}>
          <BudgetServiceItem
            type={type}
            index={index}
            title={BudgetItemTypeEnum[type as keyof typeof BudgetItemTypeEnum]}
            serviceData={service}
            currency={exchangeCurrency || "BRL"}
            categoryExchange={categoryExchange}
            descriptionFocus={descriptionFocus}
            setDescriptionFocus={setDescriptionFocus}
            serviceFocus={serviceFocus}
            setServiceFocus={setServiceFocus}
            mainAppliedTaxes={mainAppliedTaxes}
            onTransferService={onTransferService}
          />
        </Box>
      </Box>
      {hoveredServiceIndex === index && !disabled && (
        <Divider
          sx={{
            cursor: "pointer",
            color: "#1361A4",
            fontSize: "13px",
            "&:hover": {
              color: "#f0f7ff",
            },
            transition: "all 0.2s ease",
          }}
          onMouseDown={(event) => {
            // Lock before blur. Blur remaps the list and Chrome retargets
            // this press onto the chip that just mounted under the cursor;
            // each insert has a new index, so the gesture would otherwise
            // add a service on every pass.
            if (!prepareAddServiceFromPointer(event)) return;
            handleAddServiceAtPosition(index + 1);
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <Chip
            component="span"
            label="+ Novo serviço"
            color="primary"
            size="small"
          />
        </Divider>
      )}
    </Box>
  );
};

// Bail out only when every prop is reference-equal. With Stage 1 +
// `React.memo(BudgetServiceItem)` the parent already keeps each row's
// `service` reference stable across edits in other rows; making the
// surrounding `SortableServiceItem` memoized prevents `useSortable` from
// running for every row on every keystroke too.
const SortableServiceItem = React.memo(SortableServiceItemImpl);

const BudgetItem: React.FC<Props> = ({
  type,
  supplierId,
  paymentInfo,
  supplierName,
  groupId,
  totalsOverride,
  headerSubtitle,
  headerLeadingSlot,
  expanded,
  onChange,
  onRemoveItem,
  onMoveBillingModel,
  onTransferSupplier,
  panel,
  index,
  onChangeBaseValue,
  baseValuePanel,
  baseValueExpanded,
}: Props) => {
  const {
    activeCalcOption,
    detailedBudget,
    versionInfo,
    isBudgetDisabled,
    activeBudgetId,
  } = useBudgetMeta();
  const { calcTotalsSource: totals, calcTotalsCompare: comparison } =
    useCategoryTotals(supplierId, type);
  const { activeBudgetDispatch, updateBudget, runBudgetAutosave } =
    useBudgetActions();
  const { budgetServices, servicesByGroupKey } = useBudgetServicesState();
  const {
    baseValues,
    mainAppliedTaxes: mainAppliedTaxesList,
    files,
    supplierList,
    addedItemTypeList,
  } = useBudgetSuppliersState();
  const { tabActive, project, event, setTabActive } = useProject();
  // const itemTotal = activeBudget?.budgetValuesTotal?.[type]?.totalInBRL || 0;
  // uma lista com os serviços que são do tipo e fornecedor recebidos

  const baseValue = baseValues?.find(
    (el) =>
      el.calcOption === activeCalcOption &&
      el.supplierId === supplierId &&
      el.category === type &&
      el.version === versionInfo.version
  );

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const projectId = searchParams.get("projectId");

  const activeVersion = useMemo(() => {
    const parts = detailedBudget.split("-");
    const parsed = Number(parts[2]);
    return Number.isFinite(parsed) ? parsed : versionInfo.version;
  }, [detailedBudget, versionInfo.version]);

  const serviceGroupKey = getBudgetServiceGroupKey(
    activeBudgetId || "",
    supplierId,
    type,
    activeCalcOption,
    activeVersion
  );

  // Memoized service selector backed by the normalized Services slice.
  const servicesOfPair = servicesByGroupKey[serviceGroupKey] || [];

  // Na visão agrupada a linha mostra só os serviços do SEU agrupamento — o
  // mesmo par pode ter serviços em vários. Filtrar aqui é seguro porque toda
  // posição desta lista é traduzida de volta para o índice absoluto de
  // `budgetServices` por IDENTIDADE (ver `handleAddServiceAtPosition` e o
  // `handleDragEnd` de serviços), nunca por aritmética de índice.
  const filteredServicesList = useMemo(
    () =>
      groupId === undefined
        ? servicesOfPair
        : servicesOfPair.filter((service) => service.groupId === groupId),
    [servicesOfPair, groupId]
  );

  // Pega câmbio baseado na versão ativa
  const baseValueExchange = useMemo(() => {
    // Se a categoria teve o câmbio fixado manualmente (override), o valor
    // autoritativo é o de baseValues — é exatamente o que o cálculo usa
    // (ver resolvedExchange/baseIsManualOverride em calculateAndMapService).
    // Ler daqui evita que o campo "volte" para o câmbio da versão no blur.
    if (baseValue?.isOverride && baseValue?.exchange) {
      return baseValue.exchange;
    }

    if (versionInfo.version > 0) {
      const sampleService = filteredServicesList.find(
        (s) => s.negotiationValue?.[versionInfo.version - 1]?.exchangeData
      );

      if (
        sampleService?.negotiationValue?.[versionInfo.version - 1]?.exchangeData
      ) {
        return sampleService.negotiationValue[versionInfo.version - 1]
          .exchangeData;
      }
    }

    return baseValue?.exchange;
  }, [filteredServicesList, versionInfo.version, baseValue]);

  /** Nacional não exibe Câmbio na categoria; baseValues podem manter moeda antiga até sync — para UI fiscal usa-se BRL. */
  const effectiveCategoryExchange = useMemo(() => {
    if (event?.eventScope === EventScopeEnum.NATIONAL) {
      return { date: "", currency: "BRL" as const, value: 1 };
    }
    return (
      baseValueExchange || {
        currency: "BRL" as const,
        date: "",
        value: 1,
      }
    );
  }, [event?.eventScope, baseValueExchange]);

  const [compareBaseValues, setCompareBaseValues] = useState(
    {} as ServiceValues
  );

  useEffect(() => {
    if (!baseValue) {
      // Obtém o câmbio do evento para replicar na categoria
      const eventExchange = getEventExchange(event);

      activeBudgetDispatch({
        type: "INIT_BASE_VALUES",
        payload: {
          supplierId,
          type,
          calcOption: activeCalcOption,
          eventExchange, // Passa o câmbio do evento (ou null se Nacional)
        },
        versionInfo,
      });
    }
  }, [baseValue, supplierId, type, activeCalcOption, versionInfo]); // Removido 'event' para evitar loop

  function handleAddNewService() {
    dispatchAddService(activeBudgetDispatch, {
      type: "ADD_SERVICE",
      payload: {
        supplierId,
        supplierName,
        type,
        tabActive,
        calcOption: activeCalcOption,
        groupId,
        versionInfo,
        projectEvent: project?.event,
      },
    });
  }

  // Lists are read from refs so this callback stays stable across
  // ADD_SERVICE / UPDATE_CALC_VALUES. A new function identity here used to
  // remount the chip mid-click and let the same native event fire again.
  const filteredServicesListRef = useRef(filteredServicesList);
  filteredServicesListRef.current = filteredServicesList;
  const budgetServicesRef = useRef(budgetServices);
  budgetServicesRef.current = budgetServices;

  const handleAddServiceAtPosition = useCallback(
    (positionInFilteredList: number) => {
      const currentFiltered = filteredServicesListRef.current;
      const currentServices = budgetServicesRef.current;
      let insertAtIndex: number | undefined;
      if (positionInFilteredList >= currentFiltered.length) {
        insertAtIndex = undefined;
      } else {
        const targetServiceInFiltered = currentFiltered[positionInFilteredList];
        const fullArrayIndex = currentServices?.findIndex(
          (service) => service === targetServiceInFiltered
        );
        insertAtIndex =
          fullArrayIndex !== undefined && fullArrayIndex >= 0
            ? fullArrayIndex
            : undefined;
      }

      dispatchAddService(activeBudgetDispatch, {
        type: "ADD_SERVICE",
        payload: {
          supplierId,
          supplierName,
          type,
          tabActive,
          calcOption: activeCalcOption,
          insertAtIndex,
          groupId,
          versionInfo,
          projectEvent: project?.event,
        },
      });
    },
    [
      activeBudgetDispatch,
      supplierId,
      supplierName,
      type,
      groupId,
      tabActive,
      activeCalcOption,
      versionInfo,
      project?.event,
    ]
  );

  /* ---------------------------------------------------------------------- *
   * Transferir serviço para outro fornecedor
   *
   * O modal vive aqui, e não no `ServiceItem`: aquele leaf é uma ilha de
   * performance que de propósito não consome `supplierList`/`providersById`
   * (ver o comentário sobre `useBudgetMeta` em `ServiceItem`). Ele só emite o
   * callback memoizado abaixo.
   * ---------------------------------------------------------------------- */
  const [transferService, setTransferService] = useState<ServiceData | null>(
    null
  );

  const openTransferService = useCallback(
    (service: ServiceData) => setTransferService(service),
    []
  );

  const transferTargets = useTransferTargets(activeCalcOption, supplierId);

  /**
   * A categoria já existe no fornecedor de destino? Decide o texto de
   * confirmação — e, no reducer, se o perfil fiscal da origem será clonado.
   */
  const categoryExistsIn = useCallback(
    (targetSupplierId: string) =>
      (addedItemTypeList ?? []).some(
        (el) =>
          el.type === type &&
          el.supplierId === targetSupplierId &&
          el.calcOption === activeCalcOption
      ),
    [addedItemTypeList, type, activeCalcOption]
  );

  /**
   * Id interno que o destino terá na `supplierList`. Fornecedor novo que já
   * ocupe um id nesta forma de cobrança precisa de id próprio, senão as duas
   * linhas disputam a mesma identidade.
   */
  const resolveTargetSupplierId = useCallback(
    (target: TransferTarget) =>
      target.isNew
        ? resolveNewSupplierId(
            target.realSupplierId,
            activeCalcOption as CalcOption,
            supplierList,
            { isTemporary: target.isTemporary }
          )
        : target.existingSupplierId ?? target.realSupplierId,
    [activeCalcOption, supplierList]
  );

  /** Rótulo do serviço para os textos do modal. */
  const transferServiceLabel = useMemo(() => {
    if (!transferService) return "";
    const values =
      getPhaseValuesFromService(transferService, versionInfo) ||
      transferService.budgetValues;
    const serviceType = values?.serviceType;
    if (!serviceType) return "Serviço sem descrição";
    // Categorias de texto livre guardam o próprio rótulo; as demais guardam o
    // `value` da opção e o rótulo vem de `ServiceOptions`.
    const option = ServiceOptions[type]?.find(
      (el: any) => el.value === serviceType
    );
    return option?.text || serviceType;
  }, [transferService, type, versionInfo]);

  /**
   * Valor atual × valor projetado do serviço no destino, calculado ANTES do
   * dispatch. Entrar numa categoria que já existe significa adotar o perfil
   * fiscal dela, e o usuário precisa ver o número em vez de só um aviso.
   */
  const projectTransferValue = useCallback(
    (target: TransferTarget) => {
      const service = transferService;
      if (!service?._id) return null;

      const toSupplierId = resolveTargetSupplierId(target);
      if (!toSupplierId || toSupplierId === supplierId) return null;

      return projectTransferredServiceValue(
        {
          budgetServices,
          baseValues,
          mainAppliedTaxes: mainAppliedTaxesList,
          supplierList,
          addedItemTypeList,
        },
        {
          serviceId: service._id,
          type,
          calcOption: activeCalcOption as CalcOption,
          fromSupplierId: supplierId,
          toSupplierId,
          toSupplierName: target.supplierName,
          version: versionInfo.version,
        },
        versionInfo
      );
    },
    [
      transferService,
      resolveTargetSupplierId,
      budgetServices,
      baseValues,
      mainAppliedTaxesList,
      supplierList,
      addedItemTypeList,
      type,
      activeCalcOption,
      supplierId,
      versionInfo,
    ]
  );

  const confirmTransferService = useCallback(
    async (target: TransferTarget): Promise<boolean> => {
      const service = transferService;
      if (!service?._id) return false;

      const toSupplierId = resolveTargetSupplierId(target);

      if (!toSupplierId || toSupplierId === supplierId) return false;

      activeBudgetDispatch({
        type: "TRANSFER_SERVICE_SUPPLIER",
        payload: {
          serviceId: service._id,
          type,
          typeName: BudgetItemTypeEnum[type as keyof typeof BudgetItemTypeEnum],
          calcOption: activeCalcOption as CalcOption,
          fromSupplierId: supplierId,
          toSupplierId,
          toSupplierName: target.supplierName,
        },
        versionInfo,
      });

      if (!activeBudgetId) return true;

      return new Promise<boolean>((resolve) => {
        // `runBudgetAutosave` lê de `latestActiveBudgetRef.current`, que só
        // reflete o novo estado depois do commit do reducer.
        window.setTimeout(() => {
          void (async () => {
            const ok = await runBudgetAutosave("manual", {
              actionType: "TRANSFER_SERVICE",
              auditContext: {
                "Serviço transferido": transferServiceLabel,
                Categoria:
                  BudgetItemTypeEnum[type as keyof typeof BudgetItemTypeEnum],
                "Fornecedor de origem": supplierName || supplierId,
                "Fornecedor de destino": target.supplierName,
                "Forma de cobrança": activeCalcOption,
              },
            });
            if (!ok) {
              toast.error(
                "Não foi possível salvar após transferir o serviço. Verifique sua conexão e tente salvar de novo."
              );
            }
            resolve(ok);
          })();
        }, 0);
      });
    },
    [
      transferService,
      transferServiceLabel,
      resolveTargetSupplierId,
      activeBudgetDispatch,
      runBudgetAutosave,
      supplierId,
      supplierName,
      type,
      activeCalcOption,
      versionInfo,
      activeBudgetId,
    ]
  );

  function defineFiles() {
    const found = files?.find(
      (el) =>
        el.calcOption === activeCalcOption &&
        el.supplierId === supplierId &&
        el.type === type
    );
    return found || ({} as AttachedFiles);
  }

  const mainAppliedTaxes = useMemo(() => {
    const savedMainAppliedTaxes = mainAppliedTaxesList?.find(
      (el) =>
        el.calcOption === activeCalcOption &&
        el.supplierId === supplierId &&
        el.category === type
    );
    if (!savedMainAppliedTaxes) {
      activeBudgetDispatch({
        type: "INIT_MAIN_APPLIED_TAXES",
        payload: {
          supplierId,
          category: type,
          calcOption: activeCalcOption,
          version: versionInfo.version,
        },
      });
    }
    return (
      savedMainAppliedTaxes ||
      ({
        appliedTaxes: {
          serviceTax: true,
          iss: true,
          cbs: true,
          ibs: true,
          tribute: true,
          supplier_tribute: true,
          localTax: true,
          tourismTax: true,
          fee: true,
          iof: true,
          markup: true,
          overhead: true,
          commission: true,
          incentive: true,
          doubleCharged: true,
        },
        calcOption: activeCalcOption,
        supplierId,
        category: type,
        version: versionInfo.version,
      } as MainAppliedTaxes)
    );
  }, [
    mainAppliedTaxesList,
    activeCalcOption,
    supplierId,
    type,
    versionInfo,
    activeBudgetDispatch,
  ]);

  function handleUpdateBaseMonetary(value: any, field: any) {
    // Verifica se o valor realmente mudou antes de disparar o dispatch
    const currentTaxData = baseValue?.taxData;
    if (JSON.stringify(currentTaxData) === JSON.stringify(value)) {
      // Valor não mudou, não precisa disparar atualização
      return;
    }

    const updatedBaseValue = { ...baseValue };

    let newBaseValues = updatedBaseValue;
    newBaseValues.taxData = value;

    activeBudgetDispatch({
      type: "UPDATE_BASE_CALC_VALUES",
      payload: {
        value,
        supplierId,
        type,
        calcOption: activeCalcOption,
        field: "taxData",
      },
      versionInfo,
    });
  }

  // Data e moeda da categoria seguem sempre Dados Cadastrais do Evento; apenas o
  // valor do câmbio pode ser personalizado por categoria (ícone de editar +
  // Confirmar). O override marcado aqui é o que protege a categoria do sync
  // automático disparado quando o câmbio do evento muda (UPDATE_BASE_VALUES_EXCHANGE).
  function confirmCustomExchange(
    newCurrency: string,
    newValue: number,
    newDate?: string
  ) {
    const eventExchange = getEventExchange(event);
    const confirmedDate =
      newDate ?? baseValueExchange?.date ?? eventExchange?.date;

    if (
      baseValueExchange?.currency === newCurrency &&
      baseValueExchange?.value === newValue &&
      baseValueExchange?.date === confirmedDate
    ) {
      // Nada mudou, não precisa disparar atualização
      return;
    }

    activeBudgetDispatch({
      type: "UPDATE_BASE_CALC_VALUES",
      payload: {
        value: {
          currency: newCurrency || eventExchange?.currency,
          value: newValue,
          date: confirmedDate,
          isOverride: true,
        },
        supplierId,
        type,
        calcOption: activeCalcOption,
        field: "exchange",
      },
      versionInfo,
    });
    toast.success("Câmbio personalizado para esta categoria!");
  }

  function resetExchangeToDefault() {
    const eventExchange = getEventExchange(event);
    if (eventExchange) {
      activeBudgetDispatch({
        type: "UPDATE_BASE_CALC_VALUES",
        payload: {
          value: {
            ...eventExchange,
            isOverride: false,
          },
          supplierId,
          type,
          calcOption: activeCalcOption,
          field: "exchange",
        },
        versionInfo,
      });
      toast.success("Câmbio restaurado para o padrão do evento!");
    }
  }

  // Função para navegar até Dados Cadastrais - Dados do Evento
  function handleNavigateToEvent() {
    if (setTabActive) {
      // PRIMEIRO: Adiciona o parâmetro na URL ANTES de mudar a aba
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set("subTab", "event");
      window.history.replaceState({}, "", currentUrl.toString());

      // SEGUNDO: Muda para a aba Dados Cadastrais
      // O ProjectStep vai ler o parâmetro ao ser renderizado
      setTabActive(TabActive.INITIAL);

      toast.info("Navegando para Dados do Evento");
    }
  }

  const [descriptionFocus, setDescriptionFocus] = useState(null);
  const [serviceFocus, setServiceFocus] = useState(null);
  const [hoveredServiceIndex, setHoveredServiceIndex] = useState<number | null>(
    null
  );
  const [serviceListScrollTop, setServiceListScrollTop] = useState(0);
  const shouldVirtualizeServiceList =
    filteredServicesList.length > SERVICE_LIST_VIRTUALIZATION_THRESHOLD;
  const virtualStartIndex = shouldVirtualizeServiceList
    ? Math.max(
        0,
        Math.floor(serviceListScrollTop / SERVICE_ROW_ESTIMATED_HEIGHT) -
          SERVICE_LIST_OVERSCAN
      )
    : 0;
  const virtualVisibleCount =
    Math.ceil(SERVICE_LIST_MAX_HEIGHT / SERVICE_ROW_ESTIMATED_HEIGHT) +
    SERVICE_LIST_OVERSCAN * 2;
  const virtualEndIndex = shouldVirtualizeServiceList
    ? Math.min(
        filteredServicesList.length,
        virtualStartIndex + virtualVisibleCount
      )
    : filteredServicesList.length;
  const virtualizedServices = useMemo(
    () =>
      filteredServicesList
        .slice(virtualStartIndex, virtualEndIndex)
        .map((service, offset) => ({
          service,
          index: virtualStartIndex + offset,
        })),
    [filteredServicesList, virtualStartIndex, virtualEndIndex]
  );
  const topVirtualSpacerHeight = shouldVirtualizeServiceList
    ? virtualStartIndex * SERVICE_ROW_ESTIMATED_HEIGHT
    : 0;
  const bottomVirtualSpacerHeight = shouldVirtualizeServiceList
    ? (filteredServicesList.length - virtualEndIndex) *
      SERVICE_ROW_ESTIMATED_HEIGHT
    : 0;

  // Setup drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require dragging 8px before activating
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end event
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const resolveSortableId = (service: any, idx: number) =>
        service._id ?? `service-${idx}-${type}`;

      // Find the old and new indices in the filtered list
      const oldIndexFiltered = filteredServicesList.findIndex(
        (service, idx) => resolveSortableId(service, idx) === active.id
      );
      const newIndexFiltered = filteredServicesList.findIndex(
        (service, idx) => resolveSortableId(service, idx) === over.id
      );

      // Map to full array indices
      const oldServiceInFull = filteredServicesList[oldIndexFiltered];
      const newServiceInFull = filteredServicesList[newIndexFiltered];

      const oldIndexFull = budgetServices?.findIndex(
        (service) => service === oldServiceInFull
      );
      const newIndexFull = budgetServices?.findIndex(
        (service) => service === newServiceInFull
      );

      if (
        oldIndexFull !== undefined &&
        newIndexFull !== undefined &&
        oldIndexFull >= 0 &&
        newIndexFull >= 0
      ) {
        activeBudgetDispatch({
          type: "REORDER_SERVICES",
          payload: {
            oldIndex: oldIndexFull,
            newIndex: newIndexFull,
          },
        });

        // Sem isto a reordenação só chegava ao banco pelo autosave de intervalo
        // (60s) ou pelo botão Salvar: o dispatch marca o orçamento como sujo via
        // contador de revisão, mas quem alimenta `hasPendingBudgetChanges` — o
        // flag que faz a troca de aba dar flush — é o próprio autosave. Trocar
        // de aba logo após arrastar descartava a nova ordem.
        // `setTimeout(0)` deixa o reducer aplicar o estado antes do save ler o
        // orçamento; é o mesmo padrão das demais mutações desta tela.
        if (activeBudgetId) {
          window.setTimeout(() => {
            void runBudgetAutosave("manual");
          }, 0);
        }
      }
    }
  };

  return (
    <>
      <Accordion
        sx={categoryAccordionSx}
        expanded={expanded}
        onChange={onChange(panel)}
        TransitionProps={categoryAccordionTransitionProps}
      >
        <AccordionSummary
          aria-controls="panel1a-content"
          expandIcon={<BiChevronDown color="white" />}
          id="panel1a-header"
          style={{
            backgroundColor: CATEGORY_ROW_BACKGROUND,
            borderBottom: "1px solid #eee",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <CategoryAccordionSummary
            type={type}
            index={index}
            // Só o CABEÇALHO é recortado pelo agrupamento. O painel "Valores"
            // abaixo continua com os totais da categoria inteira: `baseValues`
            // é por (fornecedor, categoria, forma, versão) e não conhece
            // agrupamento — recortá-lo mostraria o rateio de uma parametrização
            // que não é parcial.
            totals={totalsOverride ?? totals}
            comparison={comparison}
            tabActive={tabActive}
            baseValueExchangeCurrency={effectiveCategoryExchange.currency}
            isBudgetDisabled={isBudgetDisabled}
            onMoveBillingModel={onMoveBillingModel}
            onTransferSupplier={onTransferSupplier}
            onRemoveItem={onRemoveItem}
            subtitle={headerSubtitle}
            leadingSlot={headerLeadingSlot}
            hideSavings={!!totalsOverride}
          />
        </AccordionSummary>
        <Box>{/* total summary */}</Box>

        <AccordionDetails style={{ backgroundColor: "white" }}>
          <Box
            display={"flex"}
            flexDirection={"column"}
            justifyContent={"center"}
            gap={1}
          >
            {budgetServices?.length > 0 ? (
              <Box
                sx={
                  shouldVirtualizeServiceList
                    ? {
                        maxHeight: SERVICE_LIST_MAX_HEIGHT,
                        overflowY: "auto",
                        pr: 1,
                      }
                    : undefined
                }
                onScroll={
                  shouldVirtualizeServiceList
                    ? (event) =>
                        setServiceListScrollTop(event.currentTarget.scrollTop)
                    : undefined
                }
              >
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={filteredServicesList.map(
                      (service, idx) => service._id ?? `service-${idx}-${type}`
                    )}
                    strategy={verticalListSortingStrategy}
                  >
                    {topVirtualSpacerHeight > 0 && (
                      <Box height={topVirtualSpacerHeight} />
                    )}
                    {virtualizedServices.map(({ service, index }) => {
                      return (
                        <SortableServiceItem
                          key={
                            service._id
                              ? `${service._id}-${detailedBudget}`
                              : `${type}-${index}-${detailedBudget}`
                          }
                          service={service}
                          index={index}
                          type={type}
                          baseValueExchange={baseValueExchange}
                          exchangeCurrency={effectiveCategoryExchange.currency}
                          categoryExchange={effectiveCategoryExchange}
                          descriptionFocus={descriptionFocus}
                          serviceFocus={serviceFocus}
                          setDescriptionFocus={setDescriptionFocus}
                          setServiceFocus={setServiceFocus}
                          hoveredServiceIndex={hoveredServiceIndex}
                          setHoveredServiceIndex={setHoveredServiceIndex}
                          handleAddServiceAtPosition={
                            handleAddServiceAtPosition
                          }
                          disabled={isBudgetDisabled}
                          mainAppliedTaxes={mainAppliedTaxes?.appliedTaxes}
                          onTransferService={openTransferService}
                        />
                      );
                    })}
                    {bottomVirtualSpacerHeight > 0 && (
                      <Box height={bottomVirtualSpacerHeight} />
                    )}
                  </SortableContext>
                </DndContext>
              </Box>
            ) : (
              <Box margin={"auto"}>
                <h4>Nenhum serviço salvo para {BudgetItemTypeEnum[type]} </h4>
              </Box>
            )}

            <Button
              sx={{
                ml: "auto",
                my: 2,
                width: "100%",
                borderWidth: "1px",
                borderColor: "#1361A4",
                borderStyle: "dashed",
                backgroundColor: "white",
              }}
              onMouseDown={(event) => {
                if (!prepareAddServiceFromPointer(event)) return;
                handleAddNewService();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                // Enter/Space fires click without mousedown (detail === 0).
                if (event.detail !== 0) return;
                handleAddNewService();
              }}
              variant="outlined"
              startIcon={<HiOutlinePlus />}
              disabled={isBudgetDisabled}
            >
              Adicionar serviço
            </Button>
          </Box>

          <AppliedTaxes
            calcOption={activeCalcOption as CalcOption}
            category={type}
            supplierId={supplierId}
            exchangeCurrency={effectiveCategoryExchange.currency}
            eventScope={event?.eventScope}
          />
          <Accordion
            sx={{
              ...categoryAccordionSx,
              borderRadius: "8px",
              border: "1px solid #1361A4",
              boxShadow: "none",
            }}
            expanded={baseValueExpanded}
            onChange={onChangeBaseValue(baseValuePanel)}
            TransitionProps={categoryAccordionTransitionProps}
          >
            <AccordionSummary
              aria-controls="panel1a-content"
              expandIcon={<BiChevronDown />}
              id="panel1a-header"
              style={{
                backgroundColor: "#FAFDFF",
                borderBottom: "1px solid #eee",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box>
                <span style={{ color: "#1361A4", fontWeight: 600 }}>
                  Valores
                </span>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Defina valores, taxas e tarifas para esta categoria.
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails style={{ backgroundColor: "white" }}>
              <BaseMonetaryData
                taxData={baseValue?.taxData}
                // compareTaxData={compareServiceValues?.taxData}
                exchange={effectiveCategoryExchange}
                compareExchange={
                  compareBaseValues?.exchangeData || {
                    currency: "BRL",
                    date: "",
                    value: 1,
                  }
                }
                onChange={handleUpdateBaseMonetary}
                itemType={type}
                totals={totals}
                calcOption={activeCalcOption as CalcOption}
              />

              {(event?.eventScope === EventScopeEnum.INTERNATIONAL ||
                event?.eventScope === EventScopeEnum.BOTH) && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid #E3E3E3",
                    padding: 16,
                    borderRadius: 8,
                    marginTop: 8,
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1}
                  >
                    <span style={{ fontWeight: "bold", fontSize: "14px" }}>
                      Câmbio
                    </span>
                    {baseValue?.isOverride && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label="Personalizado"
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                        {!isBudgetDisabled && (
                          <Button
                            onClick={resetExchangeToDefault}
                            startIcon={<BiReset />}
                            variant="outlined"
                            size="small"
                            color="secondary"
                          >
                            Restaurar padrão
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>

                  <ExchangeFields
                    date={baseValueExchange?.date || ""}
                    currency={baseValueExchange?.currency || "BRL"}
                    value={baseValueExchange?.value || 1}
                    onExchangeChange={confirmCustomExchange}
                    disabled={isBudgetDisabled}
                    showTitle={false}
                    eventCurrency={event?.exchangeCurrency}
                    showSyncMessage={!isBudgetDisabled}
                    onNavigateToEvent={handleNavigateToEvent}
                    gatedValueEdit
                    isCustom={Boolean(baseValue?.isOverride)}
                  />
                </div>
              )}

              <BaseValuesTotal
                totals={totals}
                showExtras={false}
                displayCurrencyCode={effectiveCategoryExchange.currency}
              />
            </AccordionDetails>
          </Accordion>
        </AccordionDetails>
      </Accordion>

      {!!transferService && (
        <TransferServiceSupplierModal
          open
          onClose={() => setTransferService(null)}
          serviceLabel={transferServiceLabel}
          categoryLabel={
            BudgetItemTypeEnum[type as keyof typeof BudgetItemTypeEnum]
          }
          currentSupplierLabel={supplierName || "Fornecedor sem nome"}
          currentSupplierRealId={parseSupplierId(supplierId)}
          calcOption={activeCalcOption as CalcOption}
          existingSuppliers={transferTargets}
          categoryExistsIn={categoryExistsIn}
          projectValue={projectTransferValue}
          onConfirm={confirmTransferService}
        />
      )}
    </>
  );
};

// All `BudgetItem` props are now stable references (primitives + booleans
// from the parent's local state, plus `useCallback`-wrapped handlers from
// `BudgetSupplier`). Wrapping in `React.memo` lets the category accordion
// skip work whenever the budget reducer fires for a sibling category /
// supplier, which is the common case while users edit a long list of
// services. Internal slice subscriptions still wake it up correctly when
// its own data changes.
export default React.memo(BudgetItem);
